/**
 * The parse of a pasted connection string as the start view's detection card and the pane's paste field show it (Option
 * C, C3): what each field will hold — values already made safe by `buildPastePreview`, every secret a fixed mask — what
 * the string could not map (left out), why an Azure Storage string is unusable, how a SQL Server `Encrypt=Strict` is
 * honoured, and what the form will still need.
 * `connection-string-parse-status` keeps its `data-outcome` (`full` / `partial` / `failed`).
 */
import { Fragment } from 'react';
import { formatLabel, listLabels, PASTE_FAILED_MESSAGE, PASTE_FORMATS_LINE, PASTE_NOT_RECOGNIZED, pasteFailedMessage, type PastePreview } from './pastePreview';
import type { PasteRefusal } from './pasteRefusalMessages';

type Props = {
  preview: PastePreview | null;
  failed: boolean;
  /** A failed parse the rules refused (C3-13, C3-21) says why instead of listing the formats. */
  refusal?: PasteRefusal;
  /** The pane's paste field names the engine and format here (the start view's card has its own title). */
  headline?: boolean;
};

export function PasteParseStatus({ preview, failed, refusal, headline = false }: Props) {
  if (failed) {
    // Guidance, not an error (final UX review UX-15): the warning tone; the formats it reads on their own muted line.
    const message = pasteFailedMessage(refusal);
    const unrecognized = message === PASTE_FAILED_MESSAGE;
    return (
      <p className="break-words text-xs text-warning" data-testid="connection-string-parse-status" data-outcome="failed" data-refusal={refusal?.refusal}>
        {unrecognized ? (
          <>
            {PASTE_NOT_RECOGNIZED}{' '}
            <span className="block text-muted-foreground">{PASTE_FORMATS_LINE}</span>
          </>
        ) : (
          message
        )}
      </p>
    );
  }
  if (!preview) return null;
  return (
    <div className="space-y-1.5 text-xs leading-snug" data-testid="connection-string-parse-status" data-outcome={preview.outcome} data-engine={preview.engine}>
      {headline && (
        <p className="text-sm font-medium" data-testid="connection-string-parse-headline">
          {preview.headline}
          {formatLabel(preview.format) && <span className="font-normal text-muted-foreground"> · {formatLabel(preview.format)}</span>}
        </p>
      )}
      {preview.rows.length > 0 && (
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5" data-testid="connection-string-preview-rows">
          {preview.rows.map((row) => (
            <Fragment key={row.field}>
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="break-words font-mono" data-field={row.field} data-secret={row.secret ? 'true' : undefined}>
                {row.value}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
      {preview.problem && (
        <p className="break-words text-warning" data-testid="connection-string-problem">
          Can’t be used as it is: {preview.problem}.
        </p>
      )}
      {preview.unmapped.length > 0 && (
        // Options the app does not need: muted (final UX review UX-14); the warning tone is for `problem` only.
        <p className="break-words text-muted-foreground" data-testid="connection-string-unmapped">
          Not mapped, left out: {preview.unmapped.join(', ')}.
        </p>
      )}
      {preview.stillNeeded.length > 0 && (
        <p className="text-muted-foreground" data-testid="connection-string-still-needed">
          Still needed: {listLabels(preview.stillNeeded)}.
        </p>
      )}
      {preview.tlsNote && (
        <p className="text-muted-foreground" data-testid="connection-string-tls-note">
          {preview.tlsNote}
        </p>
      )}
      {preview.entraHint && <p className="text-muted-foreground">Azure SQL? Consider Microsoft Entra authentication.</p>}
    </div>
  );
}
